import type { FunctionComponent, ReactNode, Ref } from 'react'
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react'

import { invariant } from '@app/utils/invariant'

import { useSchemeContext } from '@app/packages/SchemeContext/SchemeContext'

import { useEvent } from '@app/hooks/useEvent'
import { useToggle } from '@app/hooks/useToggle'

import type { Label } from './helpers'
import { RichLabel, useMode } from './helpers'

import classes from './helpers.module.scss'

const LabelRenderer: FunctionComponent<{ label: Label }> = ({ label }) => {
  const mode = useMode()
  if (label instanceof RichLabel) {
    const text = mode === 'user' ? (label.localizedLabel ?? label.label) : label.label
    return <span className={classes.label}>{text}</span>
  }
  return <span className={classes.label}>{label}</span>
}

const HelpIcon: FunctionComponent<{ help: string }> = ({ help }) => {
  const ref = useRef<HTMLDialogElement>(null)
  const open = useEvent(() => ref.current?.showModal())
  const close = useEvent(() => ref.current?.close())
  const handleBackdropClick = useEvent((e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) ref.current.close()
  })
  return (
    <Fragment>
      <button className={classes.help_icon} onClick={open} type="button">
        ?
      </button>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <dialog className={classes.help_dialog} onClick={handleBackdropClick} ref={ref}>
        <div className={classes.help_text}>
          {help.split('\n').map((line, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </Fragment>
          ))}
        </div>
        <button className={classes.button} onClick={close} type="button">
          Close
        </button>
      </dialog>
    </Fragment>
  )
}

const getHelp = (label: Label): string | undefined => (label instanceof RichLabel ? label.help : undefined)

const useBoolean = (label: Label, initialValue: boolean = false): [boolean, ReactNode, () => unknown] => {
  const [value, toggleValue] = useToggle(initialValue)
  const help = getHelp(label)

  return [
    value,
    // eslint-disable-next-line react/jsx-key
    <span className={classes.control}>
      <button className={cn(classes.button, { [classes.button_attached]: !!help })} onClick={toggleValue}>
        <LabelRenderer label={label} />
        <span className={cn(classes.toggle, { [classes.toggle_active]: value })}>
          <span className={classes.toggle_thumb} />
        </span>
      </button>
      {help ? <HelpIcon help={help} /> : null}
    </span>,
    toggleValue,
  ]
}

const Line = () => {
  const scheme = useSchemeContext().scheme
  return <hr className={cn(classes.line, classes[`scheme_${scheme}`])} />
}

const Code: FunctionComponent<{ children?: any }> = ({ children }) => <code className={classes.code}>{JSON.stringify(children, null, 2)}</code>

const ControlsRow: FunctionComponent<{ children?: ReactNode }> = ({ children }) => <div className={classes.controls_row}>{children}</div>

const ControlsCol: FunctionComponent<{ children?: ReactNode }> = ({ children }) => <div className={classes.controls_col}>{children}</div>

interface UsePolygonListButton {
  <T extends number>(label: Label, list: readonly T[], format?: (v: T) => string, deps?: any[]): [T, ReactNode, React.Dispatch<React.SetStateAction<T>>]
  <T extends string>(label: Label, list: readonly T[], format?: (v: T) => string, deps?: any[]): [T, ReactNode, React.Dispatch<React.SetStateAction<T>>]
  <T>(label: Label, list: readonly T[], format: (v: T) => string, deps?: any[]): [T, ReactNode, React.Dispatch<React.SetStateAction<T>>]
}

const useList: UsePolygonListButton = (
  label: Label,
  list: readonly any[],
  transform?: (v: any) => any,
  deps: any[] = []
): [any, ReactNode, React.Dispatch<any>] => {
  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks-js/use-memo
  const vlist = useMemo(() => list.map((v, i) => ({ v, sv: String(i), str: transform ? transform(v) : v })), [...deps, transform])
  const [sv, setSv] = useState(vlist[0]?.sv ?? invariant('useList: list is empty'))
  const current = vlist.find(item => item.sv === sv) ?? vlist[0]
  const help = getHelp(label)

  const setValue = useCallback(
    (v: any) => {
      const found = vlist.find(item => item.v === v)
      if (found) setSv(found.sv)
    },
    [vlist]
  )

  const element = (
    <span className={classes.control}>
      <span className={classes.select_wrapper}>
        <button className={cn(classes.button, { [classes.button_attached]: !!help })} type="button">
          <LabelRenderer label={label} />
          <span className={classes.value}>{current.str}</span>
        </button>
        <select className={classes.select_overlay} onChange={e => setSv(e.target.value)} value={current.sv}>
          {vlist.map((v, i) => (
            <option key={i} value={v.sv}>
              {v.str}
            </option>
          ))}
        </select>
      </span>
      {help ? <HelpIcon help={help} /> : null}
    </span>
  )

  return [current.v, element, setValue]
}

interface UsePolygonRandomButton {
  <T extends number>(label: Label): [T, ReactNode]
  <T extends number>(label: Label, rand: (n: number, i: number) => T): [T, ReactNode]
  <T extends string>(label: Label, rand: (n: number, i: number) => T): [T, ReactNode]
  <T>(label: Label, rand: (n: number, i: number) => T, format: (val: T) => ReactNode): [T, ReactNode]
}

const useRandom: UsePolygonRandomButton = (
  label: Label,
  rand: (val: number, index: number) => any = val => val,
  format: (val: any) => ReactNode = val => val
) => {
  const [value, setValue] = useState(() => rand(Math.random(), 0))
  const index = useRef(0)
  const help = getHelp(label)

  const handleChange = useCallback(() => {
    setValue(rand(Math.random(), ++index.current))
  }, [rand])

  return [
    value,
    // eslint-disable-next-line react/jsx-key
    <span className={classes.control}>
      <button className={cn(classes.button, { [classes.button_attached]: !!help })} onClick={handleChange}>
        <LabelRenderer label={label} />
        <span className={classes.value}>{format(value)}</span>
      </button>
      {help ? <HelpIcon help={help} /> : null}
    </span>,
  ] satisfies [any, ReactNode]
}

const useSlider = (label: Label, min: number, max: number, initialValue: number, step?: number): [any, ReactNode] => {
  const [value, setValue] = useState(initialValue)
  const help = getHelp(label)

  const handleChange = useCallback(e => {
    setValue(e.target.value)
  }, [])

  return [
    value,
    // eslint-disable-next-line react/jsx-key
    <span className={classes.control}>
      <span className={cn(classes.button, { [classes.button_attached]: !!help })}>
        <LabelRenderer label={label} />
        <input max={max} min={min} onChange={handleChange} step={step} style={{ verticalAlign: 'middle' }} type="range" value={value} />{' '}
        <span className={classes.value}>{value}</span>
      </span>
      {help ? <HelpIcon help={help} /> : null}
    </span>,
  ]
}

const useDialog = (): { open: (content: ReactNode) => void; close: () => void; element: ReactNode } => {
  const ref = useRef<HTMLDialogElement>(null)
  const [content, setContent] = useState<ReactNode>(null)
  const open = useEvent((node: ReactNode) => {
    setContent(node)
    ref.current?.showModal()
  })
  const close = useEvent(() => ref.current?.close())
  const handleBackdropClick = useEvent((e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) ref.current.close()
  })
  const element = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog className={classes.dialog} onClick={handleBackdropClick} ref={ref}>
      <div className={classes.dialog_body}>{content}</div>
      <button className={classes.button} onClick={close} type="button">
        Close
      </button>
    </dialog>
  )
  return { open, close, element }
}

const OpenModalButton: FunctionComponent<{ onClick: () => unknown; ref?: Ref<HTMLButtonElement> }> = ({ onClick, ref }) => {
  const mode = useMode()
  return (
    <button onClick={onClick} ref={ref} type="button">
      {mode === 'user' ? 'Открыть модал' : 'Open modal'}
    </button>
  )
}

/** The button a modal's polygon opens it with; `ref` for a modal that points at its trigger. */
const renderOpenModal = (onClick: () => unknown, { ref }: { ref?: Ref<HTMLButtonElement> } = {}) => <OpenModalButton onClick={onClick} ref={ref} />

export const helpers = {
  useBoolean,
  useList,
  useRandom,
  useSlider,
  useMode,
  useDialog,
  Line,
  Code,
  ControlsRow,
  ControlsCol,
  RichLabel,
  renderOpenModal,
}

export type Helpers = typeof helpers
